import React, { useState, useEffect, useContext, useCallback } from 'react'
import { useNavigate, Link, useParams } from 'react-router-dom'
import axios from '../../../../config/axios'
import plus from '../../../../assets/certificates/AddParameter/blue-plus.svg'
import save from '../../../../assets/certificates/AddParameter/save.svg'
import trash from '../../../../assets/production-flow/trash.svg'
import search from '../../../../assets/production-flow/search.svg'
import { FeedbackContext } from '../../../../contexts/FeedbackContext'
import {
  MainContainer,
  StyledCard,
  StyledCardHeader,
  FormGrid,
  StyledFormGroup,
  ActionButtons,
  CancelButton,
  SaveButton,
  SubHeaderContainer,
  SectionName,
  LeftSide,
  BreadcrumbsContainer,
  CardTitle,
  AddButton,
  MaterialItemContainer,
  MaterialFields,
  InlineDeleteButton,
  DropdownList,
  DropdownItem,
  SectionButtons,
} from './style'

interface MaterialItem {
  id: number
  materialId: number
  material: string
  unitOfMeasure: 'KG' | 'MM'
  quantityToUse: string
  isAbated?: boolean
  hasInsufficientStock?: boolean
}

interface ResourceItem {
  id: number
  resourceId: number
  resource: string
  unit: string
  quantity: string
  isAbated?: boolean
}

interface ServiceItem {
  id: number
  serviceId: number
  service: string
  description: string
  observation: string
}

interface Order {
  id: number
  client: string
  items: OrderItem[]
}

interface OrderItem {
  weight: any
  id: number
  quantity?: number
  material?: { name: string }
  product?: { name: string }
  stage?: { name: string }
  resource?: { name: string }
  stock?: { description: string }
  ploomesProductName?: string  
}

interface FormState {
  codeOfOP: string
  orderNumber: string
  orderItemId: number
  item: string
  order: string
  quantity: string
  weight: string
  client: string
  materials: MaterialItem[]
  resources: ResourceItem[]
  services: ServiceItem[]
}

interface SavedStageData {
  id: number
  serviceId: number
  stageId: number
  completed: boolean
  index: number
  description?: string
  observation?: string
  stage: {
    name: string
    id: number
  }
}

const INITIAL_FORM_STATE: FormState = {
  codeOfOP: '',
  orderNumber: '',
  orderItemId: 0,
  item: '',
  order: '',
  quantity: '',
  weight: '',
  client: '',
  materials: [],
  resources: [],
  services: [],
}

const Breadcrumbs: React.FC<{ items: { label: string; path?: string }[] }> = ({
  items,
}) => (
  <BreadcrumbsContainer>
    <ol>
      {items.map((item, index) => (
        <li key={index}>
          {index === items.length - 1 ? (
            <span>{item.label}</span>
          ) : item.path ? (
            <Link to={item.path}>{item.label}</Link>
          ) : (
            <span>{item.label}</span>
          )}
        </li>
      ))}
    </ol>
  </BreadcrumbsContainer>
)

const getToken = () => localStorage.getItem('token')

const CreateOP = () => {
  const navigate = useNavigate()
  const { id: opId } = useParams<{ id: string }>()
  const isEditMode = Boolean(opId)
  const { setFeedback } = useContext(FeedbackContext)

  const [form, setForm] = useState<FormState>(INITIAL_FORM_STATE)
  const [searchMaterial, setSearchMaterial] = useState('')
  const [searchResource, setSearchResource] = useState('')
  const [searchService, setSearchService] = useState('')
  const [searchOrder, setSearchOrder] = useState('')
  const [orderOptions, setOrderOptions] = useState<Order[]>([])
  const [materialOptions, setMaterialOptions] = useState<
    { id: number; name: string }[]
  >([])
  const [resourceOptions, setResourceOptions] = useState<
    { id: number; name: string }[]
  >([])
  const [serviceOptions, setServiceOptions] = useState<
    { id: number; name: string }[]
  >([])
  const [editingMaterialIndex, setEditingMaterialIndex] = useState<
    number | null
  >(null)
  const [editingResourceIndex, setEditingResourceIndex] = useState<
    number | null
  >(null)
  const [editingServiceIndex, setEditingServiceIndex] = useState<number | null>(
    null,
  )
  const [isEditingOrder, setIsEditingOrder] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const hasMaterials = form.materials.length > 0
  const hasAbatedMaterials = form.materials.some((m) => m.isAbated)
  const hasPendingMaterials = form.materials.some((m) => !m.isAbated)
  const hasResources = form.resources.length > 0
  const hasAbatedResources = form.resources.some((r) => r.isAbated)
  const hasPendingResources = form.resources.some((r) => !r.isAbated)

  const canAddMaterial = !hasAbatedMaterials && !hasPendingResources
  const canAddResource = !hasAbatedResources && !hasPendingMaterials

  const materialMessage = hasAbatedMaterials
    ? 'Já abatidos'
    : hasPendingResources
    ? 'Remova insumos pendentes'
    : ''
  const resourceMessage = hasAbatedResources
    ? 'Já abatidos'
    : hasPendingMaterials
    ? 'Remova materiais pendentes'
    : ''

  const createEmptyMaterial = (): MaterialItem => ({
    id: Date.now(),
    materialId: 0,
    material: '',
    unitOfMeasure: 'MM',
    quantityToUse: '',
    hasInsufficientStock: false,
  })

  const createEmptyResource = (): ResourceItem => ({
    id: Date.now(),
    resourceId: 0,
    resource: '',
    unit: 'KG',
    quantity: '',
  })

  const createEmptyService = (): ServiceItem => ({
    id: Date.now(),
    serviceId: 0,
    service: '',
    description: '',
    observation: '',
  })

  const getItemDescription = (orderItem: OrderItem): string => {
    return orderItem.ploomesProductName || 'Item sem descrição'
  }

  const getClientName = (client: any): string => {
    if (!client) return ''
    if (typeof client === 'object')
      return client.companyName || client.name || ''
    if (typeof client === 'string') return client
    return ''
  }

  const processMaterials = useCallback((data: any): MaterialItem[] => {
    if (!data.stockInProduction?.length) return []

    return data.stockInProduction
      .filter((stockItem: any) => stockItem.stockId !== null)
      .map((stockItem: any) => {
        const stock = stockItem.stock || {}
        return {
          id: stockItem.id || Date.now() + Math.random(),
          materialId: stock.id || stockItem.stockId || 0,
          material: stock.description || stock.name || `Material ID: ${stock.id}`,
          unitOfMeasure: stockItem.usedUnitOfMeasurement || 'KG',
          quantityToUse: stockItem.usedQuantity?.toString() || '',
          isAbated: true,
          hasInsufficientStock: false,
        }
      })
  }, [])

   const processServices = useCallback((serviceStages: any[]): ServiceItem[] => {
    if (!serviceStages?.length) return []

    return serviceStages.map((stage: SavedStageData) => ({
      id: stage.id || Date.now() + Math.random(),
      serviceId: stage.stageId,
      service: stage.stage?.name || '',
      description: stage.description || '',
      observation: stage.observation || '',
    }))
  }, [])

  const processResources = useCallback((data: any): ResourceItem[] => {
    if (data.stockInProduction?.length > 0) {
      return data.stockInProduction
        .filter((stockItem: any) => stockItem.resourceId !== null)
        .map((stockItem: any) => {
          const resource = stockItem.resource || {}
          return {
            id: stockItem.id || Date.now() + Math.random(),
            resourceId: resource.id || stockItem.resourceId || 0,
            resource: resource.name || resource.description || `Insumo ID: ${resource.id}`,
            unit: stockItem.usedUnitOfMeasurement || 'KG',
            quantity: stockItem.usedQuantity?.toString() || '',
            isAbated: true,
          }
        })
    }

    if (data.orderItem?.resource) {
      const resource = data.orderItem.resource
      return [
        {
          id: resource.id || Date.now() + Math.random(),
          resourceId: resource.id,
          resource: resource.name || resource.description || '',
          unit: resource.stockUnit || 'KG',
          quantity: '',
          isAbated: false,
        },
      ]
    }

    return []
  }, [])

  const validateStockFormat = (stock: any, unitOfMeasure: string): { isValid: boolean; message?: string } => {
    const formatType = stock.format?.type?.toLowerCase() || 'indefinido'
    const dim1 = stock.dimension1 || 0
    const dim2 = stock.dimension2 || 0
    const lengthInMm = stock.lengthInMm || 0
    const totalWeight = stock.totalWeight || 0
    const density = stock.material?.density || 0
    
    const missingFields: string[] = []

    if (unitOfMeasure === 'KG') {
      if (totalWeight <= 0) {
        missingFields.push('peso')
      }
    } else if (unitOfMeasure === 'MM') {
      if (lengthInMm <= 0) {
        missingFields.push('comprimento')
      }
    }
    
    if (density <= 0) {
      missingFields.push('densidade')
    }

    switch (formatType) {
      case 'redondo':
      case 'sextavado':
        if (dim1 <= 0) {
          missingFields.push('dimensão 1')
        }
        break
      
      case 'quadrado':
      case 'chapa':
      case 'tubo':
      case 'anel':
        if (dim1 <= 0) {
          missingFields.push('dimensão 1')
        }
        if (dim2 <= 0) {
          missingFields.push('dimensão 2')
        }
        break
      
      case 'indefinido':
        break
      
      default:
        return {
          isValid: false,
          message: `Formato de material desconhecido: ${formatType}`
        }
    }

    if (missingFields.length > 0) {
      const fieldsList = missingFields.join(', ')
      return {
        isValid: false,
        message: `Material do tipo ${formatType} sem os seguintes campos: ${fieldsList}`
      }
    }

    return { isValid: true }
  }

  const checkMaterialAvailability = async (
    materialId: number,
    quantityToUse: number,
    unitOfMeasure: string,
  ): Promise<{ available: boolean; message?: string }> => {
    try {
      const token = getToken()
      const response = await axios.get(
        `${process.env.REACT_APP_GATEWAY_BASE_URL}/production-flow/service/utils/raw-materials/${materialId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      )

      const stock = response.data
      if (!stock) {
        return {
          available: false,
          message: 'Material não encontrado no estoque',
        }
      }

      const formatValidation = validateStockFormat(stock, unitOfMeasure)
      if (!formatValidation.isValid) {
        return {
          available: false,
          message: formatValidation.message,
        }
      }

      const availableLengthInMm = stock.lengthInMm || 0
      const availableWeight = stock.totalWeight || 0

      if (unitOfMeasure === 'KG') {
        if (quantityToUse > availableWeight) {
          return {
            available: false,
            message: `Quantidade insuficiente. Disponível: ${availableWeight.toFixed(2)} KG`,
          }
        }
      } else if (unitOfMeasure === 'MM') {
        if (quantityToUse > availableLengthInMm) {
          return {
            available: false,
            message: `Comprimento insuficiente. Disponível: ${availableLengthInMm.toFixed(2)} MM`,
          }
        }
      }

      return { available: true }
    } catch (error) {
      return { available: true }
    }
  }

  const fetchOPData = useCallback(async () => {
    if (!isEditMode || !opId) return

    setIsLoading(true)
    try {
      const token = getToken()
      const [basicResponse, serviceStagesResponse] = await Promise.all([
        axios.get(
          `${process.env.REACT_APP_GATEWAY_BASE_URL}/production-flow/service/${opId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        ),
        axios.get(
          `${process.env.REACT_APP_GATEWAY_BASE_URL}/production-flow/pcp/services-of-op/${opId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        ),
      ])

      const processedMaterials = processMaterials(basicResponse.data)
      const processedResources = processResources(basicResponse.data)

      const serviceStages = serviceStagesResponse.data || []
      const processedServices = processServices(serviceStages)

      let orderItem = basicResponse.data.orderItem
      let order = orderItem?.order

      if (!orderItem && basicResponse.data.orderItemId) {
        try {
          const orderItemResponse = await axios.get(
            `${process.env.REACT_APP_GATEWAY_BASE_URL}/steel-orders/order-item/${basicResponse.data.orderItemId}`,
            { headers: { Authorization: `Bearer ${token}` } },
          )
          orderItem = orderItemResponse.data
          order = orderItem?.order
        } catch (error) {
          //
        }
      }

      setForm({
        codeOfOP: basicResponse.data.codeOfOP,
        orderNumber: `OP-${opId}`,
        orderItemId: basicResponse.data.orderItemId || 0,
        item: orderItem ? getItemDescription(orderItem) : 'Item do pedido',
        order: (order as any)?.saleNumberPloomes || order?.id?.toString() || '',
        quantity:
          orderItem?.quantity?.toString() ||
          orderItem?.quantityToProduce?.toString() ||
          '',
        weight: orderItem?.weight?.toString() || '',
        client: getClientName(
          order?.client || order?.Client || basicResponse.data.client,
        ),
        materials: processedMaterials,
        resources: processedResources,
        services: processedServices,
      })
    } catch (error) {
      setFeedback({
        isOpen: true,
        statusOk: false,
        message: 'Erro ao carregar dados da OP',
      })
    } finally {
      setIsLoading(false)
    }
  }, [
    isEditMode,
    opId,
    processMaterials,
    processResources,
    processServices,
    setFeedback,
  ])

  useEffect(() => {
    fetchOPData()
  }, [fetchOPData])

  const fetchData = useCallback(
    async (url: string, setter: any, search?: string) => {
      try {
        const token = getToken()
        const response = await axios.get(url, {
          params: search ? { search } : {},
          headers: { Authorization: `Bearer ${token}` },
        })
        if (response.data && Array.isArray(response.data)) {
          setter(response.data)
        }
      } catch (error) {
        setter([])
      }
    },
    [],
  )

  useEffect(() => {
    if (isEditingOrder) {
      const timeoutId = setTimeout(() => {
        fetchData(
          `${process.env.REACT_APP_GATEWAY_BASE_URL}/steel-orders/order`,
          setOrderOptions,
          searchOrder,
        )
      }, 300)
      return () => clearTimeout(timeoutId)
    }
  }, [isEditingOrder, searchOrder, fetchData])

  useEffect(() => {
    if (editingMaterialIndex !== null) {
      const timeoutId = setTimeout(
        () => {
          fetchData(
            `${process.env.REACT_APP_GATEWAY_BASE_URL}/production-flow/service/utils/raw-materials`,
            setMaterialOptions,
            searchMaterial,
          )
        },
        searchMaterial ? 300 : 0,
      )
      return () => clearTimeout(timeoutId)
    }
  }, [editingMaterialIndex, searchMaterial, fetchData])

  useEffect(() => {
    if (editingResourceIndex !== null) {
      const timeoutId = setTimeout(
        () => {
          fetchData(
            `${process.env.REACT_APP_GATEWAY_BASE_URL}/production-flow/service/utils/resources`,
            setResourceOptions,
            searchResource,
          )
        },
        searchResource ? 300 : 0,
      )
      return () => clearTimeout(timeoutId)
    }
  }, [editingResourceIndex, searchResource, fetchData])

  useEffect(() => {
    if (editingServiceIndex !== null) {
      const timeoutId = setTimeout(
        () => {
          fetchData(
            `${process.env.REACT_APP_GATEWAY_BASE_URL}/production-flow/service/utils/stages`,
            setServiceOptions,
            searchService,
          )
        },
        searchService ? 300 : 0,
      )
      return () => clearTimeout(timeoutId)
    }
  }, [editingServiceIndex, searchService, fetchData])

  const selectOrder = useCallback((order: Order, orderItem: OrderItem) => {
    setForm((prev) => ({
      ...prev,
      order: (order as any).saleNumberPloomes || order.id.toString(),
      orderItemId: orderItem.id,
      item: getItemDescription(orderItem),
      client: order.client,
      weight: orderItem?.weight?.toString() || '',
    }))
    setIsEditingOrder(false)
    setSearchOrder('')
  }, [])

  const addMaterial = useCallback(() => {
    if (!canAddMaterial) {
      setFeedback({
        isOpen: true,
        statusOk: false,
        message: materialMessage || 'Não é possível adicionar material',
      })
      return
    }
    setForm((prev) => ({
      ...prev,
      materials: [...prev.materials, createEmptyMaterial()],
    }))
    setEditingMaterialIndex(form.materials.length)
    setSearchMaterial('')
  }, [canAddMaterial, materialMessage, form.materials.length, setFeedback])

  const addResource = useCallback(() => {
    if (!canAddResource) {
      setFeedback({
        isOpen: true,
        statusOk: false,
        message: resourceMessage || 'Não é possível adicionar insumo',
      })
      return
    }
    setForm((prev) => ({
      ...prev,
      resources: [...prev.resources, createEmptyResource()],
    }))
    setEditingResourceIndex(form.resources.length)
    setSearchResource('')
  }, [canAddResource, resourceMessage, form.resources.length, setFeedback])

  const addService = useCallback(() => {
    setForm((prev) => ({
      ...prev,
      services: [...prev.services, createEmptyService()],
    }))
    setEditingServiceIndex(form.services.length)
    setSearchService('')
  }, [form.services.length])

  const updateItem = useCallback(
    async (
      section: 'materials' | 'resources' | 'services',
      id: number,
      field: string,
      value: any,
    ) => {
      setForm((prev) => ({
        ...prev,
        [section]: prev[section].map((item) =>
          item.id === id ? { ...item, [field]: value } : item,
        ),
      }))

  if (section === 'materials' && (field === 'quantityToUse' || field === 'unitOfMeasure')) {
  const material = form.materials.find((m) => m.id === id)

  if (material && material.materialId > 0) {
    const quantity = field === 'quantityToUse' ? parseFloat(value.replace(',', '.')) : parseFloat(material.quantityToUse.replace(',', '.'))
    const unit = field === 'unitOfMeasure' ? value : material.unitOfMeasure

    if (quantity > 0 && !isNaN(quantity)) {
      const availability = await checkMaterialAvailability(material.materialId, quantity, unit)

      setForm((prev) => ({
        ...prev,
        materials: prev.materials.map((m) =>
          m.id === id ? { ...m, hasInsufficientStock: !availability.available } : m
        ),
      }))

      if (!availability.available) {
        setFeedback({
          isOpen: true,
          statusOk: false,
          message: availability.message || 'Quantidade insuficiente no estoque',
        })
      }
    }
  }
}
}, [form.materials, setFeedback], )

  const selectItem = useCallback(
    async (
      section: 'materials' | 'resources' | 'services',
      index: number,
      option: { id: number; name: string },
    ) => {
      const fieldMap = {
        materials: { idField: 'materialId', nameField: 'material' },
        resources: { idField: 'resourceId', nameField: 'resource' },
        services: { idField: 'serviceId', nameField: 'service' },
      }

      const { idField, nameField } = fieldMap[section]

      if (section === 'materials') {
        const material = form.materials[index]
        if (material.quantityToUse) {
          const quantity = parseFloat(material.quantityToUse.replace(',', '.'))
          if (quantity > 0 && !isNaN(quantity)) {
            const availability = await checkMaterialAvailability(
              option.id,
              quantity,
              material.unitOfMeasure,
            )
            
            setForm((prev) => ({
              ...prev,
              [section]: prev[section].map((item, i) => {
                if (i !== index) return item;

                const updatedItem = {
                  ...item,
                  [idField]: option.id,
                  [nameField]: option.name,
                  hasInsufficientStock: !availability.available,
                };

                return updatedItem;
              }),
            }))

            if (!availability.available) {
              setFeedback({
                isOpen: true,
                statusOk: false,
                message: availability.message || 'Quantidade insuficiente no estoque',
              })
            }

            setEditingMaterialIndex(null)
            setSearchMaterial('')
            return
          }
        }
      }

      setForm((prev) => ({
        ...prev,
        [section]: prev[section].map((item, i) =>
          i === index
            ? { ...item, [idField]: option.id, [nameField]: option.name }
            : item,
        ),
      }))

      setEditingMaterialIndex(null)
      setEditingResourceIndex(null)
      setEditingServiceIndex(null)
      setSearchMaterial('')
      setSearchResource('')
      setSearchService('')
    },
    [form.materials, setFeedback],
  )

  const removeItem = useCallback(
    (section: 'materials' | 'resources' | 'services', id: number) => {
      setForm((prev) => ({
        ...prev,
        [section]: prev[section].filter((item) => item.id !== id),
      }))
    },
    [],
  )

  const validateForm = useCallback(async (): Promise<string | null> => {
    if (!form.orderItemId) return 'Selecione um pedido e um item.'

    if (
      form.services.some(
        (srv) => srv.service && (!srv.serviceId || srv.serviceId === 0),
      )
    ) {
      return 'Se adicionar serviços, todos devem ter um serviço selecionado.'
    }

    const pendingMaterials = form.materials.filter((m) => !m.isAbated)
    const pendingResources = form.resources.filter((r) => !r.isAbated)

    if (pendingMaterials.length > 0 && pendingResources.length > 0) {
      return 'Não é possível ter materiais e insumos pendentes simultaneamente. Remova um dos tipos.'
    }

    if (
      form.materials.length > 0 &&
      form.materials.some(
        (mat) =>
          !mat.materialId ||
          mat.materialId === 0 ||
          !mat.quantityToUse ||
          parseFloat(mat.quantityToUse.replace(',', '.')) <= 0,
      )
    ) {
      return 'Se adicionar materiais, todos devem ter um material selecionado e quantidade válida.'
    }

    if (pendingMaterials.length > 0) {
      const allSelected = pendingMaterials.every((m) => m.materialId > 0)
      if (!allSelected) {
        return 'Todos os materiais devem ter um material selecionado antes de salvar.'
      }

      for (const material of pendingMaterials) {
        const quantity = parseFloat(material.quantityToUse.replace(',', '.'))
        if (quantity > 0 && !isNaN(quantity)) {
          const availability = await checkMaterialAvailability(
            material.materialId,
            quantity,
            material.unitOfMeasure,
          )
          if (!availability.available) {
            return availability.message || 'Quantidade insuficiente em um ou mais materiais.'
          }
        }
      }
    }

    if (
      form.resources.length > 0 &&
      form.resources.some(
        (res) =>
          !res.resourceId ||
          res.resourceId === 0 ||
          !res.quantity ||
          parseFloat(res.quantity.replace(',', '.')) <= 0,
      )
    ) {
      return 'Se adicionar insumos, todos devem ter um insumo selecionado e quantidade válida.'
    }

    if (pendingResources.length > 0) {
      const allSelected = pendingResources.every((r) => r.resourceId > 0)
      if (!allSelected) {
        return 'Todos os insumos devem ter um insumo selecionado antes de salvar.'
      }
    }

    const resourcesWithInvalidUnit = form.resources.filter(
      (res) => res.unit === 'UN' && !Number.isInteger(parseFloat(res.quantity.replace(',', '.')))
    )
    if (resourcesWithInvalidUnit.length > 0) {
      return 'Insumos com unidade "UN" devem ter quantidade em números inteiros.'
    }

    return null
  }, [form])

  const discountItemsAutomatically = async (
    opId: number,
    materials: MaterialItem[],
    resources: ResourceItem[],
  ) => {
    try {
      const token = getToken()
      
      const itemsToDiscount: any = {}

      if (materials.length > 0) {
        itemsToDiscount.materials = materials.map((item, index) => ({
          id: index + 1,
          material: {
            id: item.materialId,
            name: item.material,
          },
          quantityToUse: parseFloat(item.quantityToUse.replace(',', '.')),
          unitOfMeasure: item.unitOfMeasure,
        }))
      }

      if (resources.length > 0) {
        itemsToDiscount.resources = resources.map((item, index) => ({
          id: index + 1,
          material: {
            id: item.resourceId,
            name: item.resource,
          },
          quantityToUse: parseFloat(item.quantity.replace(',', '.')),
          unitOfMeasure: item.unit,
        }))
      }

      const response = await axios.post(
        `${process.env.REACT_APP_GATEWAY_BASE_URL}/production-flow/pcp/discount/${opId}`,
        itemsToDiscount,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          timeout: 30000,
        },
      )

      return { success: true }
    } catch (error) {
      const errorMessage = axios.isAxiosError(error)
        ? error.response?.data?.message || error.message
        : 'Erro desconhecido no abatimento'
      return { success: false, error: errorMessage }
    }
  }

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault()

    const validationError = await validateForm()
    if (validationError) {
      setFeedback({ isOpen: true, statusOk: false, message: validationError })
      return
    }

    if (!getToken()) {
      setFeedback({
        isOpen: true,
        statusOk: false,
        message: 'Sessão expirada. Faça login novamente.',
      })
      return
    }

    setIsSubmitting(true)

    try {
      const token = getToken()
      const dataToSend: any = {
        orderItemId: parseInt(form.orderItemId.toString(), 10),
        quantity: form.quantity ? parseInt(form.quantity, 10) : null,
        stages: form.services.map((service, index) => ({
          stageId: parseInt(service.serviceId.toString(), 10),
          index: index,
          description: service.description || '',
          observation: service.observation || '',
        })),
      }

      let response, createdOpId

      if (isEditMode && opId) {
        response = await axios.patch(
          `${process.env.REACT_APP_GATEWAY_BASE_URL}/production-flow/service/${opId}`,
          dataToSend,
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          },
        )
        createdOpId = parseInt(opId)
      } else {
        response = await axios.post(
          `${process.env.REACT_APP_GATEWAY_BASE_URL}/production-flow/service`,
          dataToSend,
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          },
        )
        createdOpId =
          response.data?.id ||
          (typeof response.data === 'string'
            ? parseInt(response.data.match(/OP[\s](\d+)/)?.[1] || '0')
            : 0)
      }

      let abatimentoMessage = ''
      
      const materialsToAbate = form.materials.filter((m) => !m.isAbated)
      const resourcesToAbate = form.resources.filter((r) => !r.isAbated)
      
      if (materialsToAbate.length > 0 && resourcesToAbate.length > 0) {
        setFeedback({
          isOpen: true,
          statusOk: false,
          message: 'Não é possível abater materiais e insumos simultaneamente. Remova um dos tipos antes de salvar.',
        })
        return
      }
      
      if (materialsToAbate.length > 0 || resourcesToAbate.length > 0) {
        if (createdOpId) {
          const discountResult = await discountItemsAutomatically(
            createdOpId,
            materialsToAbate,
            resourcesToAbate,
          )
          
          if (discountResult.success) {
            if (materialsToAbate.length > 0) {
              abatimentoMessage = ' Materiais abatidos automaticamente.'
            } else if (resourcesToAbate.length > 0) {
              abatimentoMessage = ' Insumos abatidos automaticamente.'
            }
          } else {
            const tipoItem = materialsToAbate.length > 0 ? 'materiais' : 'insumos'
            abatimentoMessage = ` Porém, o abatimento de ${tipoItem} falhou: ${discountResult.error}. Abata manualmente.`
          }
        }
      }

      setFeedback({
        isOpen: true,
        statusOk: true,
        message: `OP ${
          isEditMode ? 'atualizada' : 'criada'
        } com sucesso!${abatimentoMessage}`,
      })

      navigate('../')
    } catch (error) {
      const errorMessage = axios.isAxiosError(error)
        ? error.response?.data?.message || error.message
        : 'Erro desconhecido'
      setFeedback({
        isOpen: true,
        statusOk: false,
        message: `Erro ao ${
          isEditMode ? 'atualizar' : 'criar'
        } ordem de produção: ${errorMessage}`,
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCancel = useCallback(() => {
    navigate('/panel/production')
  }, [navigate])

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      if (!target.closest('.dropdown-container')) {
        setEditingMaterialIndex(null)
        setEditingResourceIndex(null)
        setEditingServiceIndex(null)
        setIsEditingOrder(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const breadcrumbItems = [
    { label: 'Ordem de produção', path: '/panel/production' },
    {
      label: isEditMode
        ? 'Editar ordem de produção'
        : 'Criar ordem de produção',
    },
  ]

  if (isLoading) {
    return (
      <MainContainer>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '400px',
          }}
        >
          <p>Carregando dados da OP {opId}...</p>
        </div>
      </MainContainer>
    )
  }

  return (
    <>
      <SubHeaderContainer>
        <LeftSide>
          <SectionName>
            {isEditMode && form.orderNumber
              ? `Ordem de produção: ${form.codeOfOP}`
              : 'Criar ordem de produção'}
          </SectionName>
        </LeftSide>
        <ActionButtons>
          <CancelButton type='button' onClick={handleCancel}>
            Cancelar
          </CancelButton>
          <SaveButton
            type='button'
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            <img src={save} alt='Salvar' />
            {isSubmitting
              ? isEditMode
                ? 'Atualizando...'
                : 'Criando OP...'
              : isEditMode
              ? 'Atualizar OP'
              : 'Salvar OP'}
          </SaveButton>
        </ActionButtons>
      </SubHeaderContainer>

      <Breadcrumbs items={breadcrumbItems} />

      <MainContainer>
        <form onSubmit={handleSubmit}>
          <StyledCard>
            <CardTitle>Geral</CardTitle>
            <FormGrid cols={2}>
              <StyledFormGroup className='dropdown-container'>
                <label htmlFor='order'>Pedido</label>
                <input
                  type='text'
                  id='order'
                  value={isEditingOrder ? searchOrder : form.order}
                  onChange={(e) => {
                    setSearchOrder(e.target.value)
                    setIsEditingOrder(true)
                  }}
                  onFocus={() => {
                    setIsEditingOrder(true)
                    setSearchOrder(form.order)
                  }}
                  disabled={isEditMode}
                />
                {isEditingOrder && orderOptions.length > 0 && (
                  <DropdownList>
                    {orderOptions.map((order: any) => (
                      <DropdownItem
                        key={order.id}
                        onClick={() =>
                          order.items?.length > 0 &&
                          selectOrder(order, order.items[0])
                        }
                      >
                        {order.saleNumberPloomes || order.id}
                      </DropdownItem>
                    ))}
                  </DropdownList>
                )}
              </StyledFormGroup>

              <StyledFormGroup>
                <label htmlFor='client'>Cliente</label>
                <input
                  type='text'
                  id='client'
                  value={form.client}
                  readOnly
                  disabled
                />
              </StyledFormGroup>

              <StyledFormGroup>
                <label htmlFor='item'>Item</label>
                <input
                  type='text'
                  id='item'
                  value={form.item}
                  readOnly
                  disabled
                />
              </StyledFormGroup>

              <StyledFormGroup>
                <label htmlFor='weight'>Peso</label>
                <input
                  type='number'
                  id='weight'
                  value={form.weight}
                  readOnly
                  disabled
                />
              </StyledFormGroup>
            </FormGrid>
          </StyledCard>

          <StyledCard>
            <StyledCardHeader>
              <CardTitle>Materiais</CardTitle>
            </StyledCardHeader>

            {form.materials.map((material, index) => (
              <MaterialItemContainer key={material.id}>
                <MaterialFields>
                  <StyledFormGroup className='dropdown-container'>
                    <label>Material</label>
                    <input
                      type='text'
                      value={
                        editingMaterialIndex === index
                          ? searchMaterial
                          : material.material
                      }
                      onChange={(e) => {
                        setSearchMaterial(e.target.value)
                        setEditingMaterialIndex(index)
                      }}
                      onFocus={() => {
                        setEditingMaterialIndex(index)
                        setSearchMaterial(material.material)
                      }}
                      disabled={material.isAbated}
                    />
                    {editingMaterialIndex === index &&
                      materialOptions.length > 0 && (
                        <DropdownList>
                          {materialOptions.map((option) => (
                            <DropdownItem
                              key={option.id}
                              onClick={() =>
                                selectItem('materials', index, option)
                              }
                            >
                              {option.name}
                            </DropdownItem>
                          ))}
                        </DropdownList>
                      )}
                  </StyledFormGroup>
                  <StyledFormGroup>
                    <label>Un. de medida</label>
                    <select
                      value={material.unitOfMeasure}
                      onChange={(e) =>
                        updateItem(
                          'materials',
                          material.id,
                          'unitOfMeasure',
                          e.target.value,
                        )
                      }
                      disabled={material.isAbated}
                    >
                      <option value='KG'>KG</option>
                      <option value='MM'>MM</option>
                    </select>
                  </StyledFormGroup>
                  <StyledFormGroup>
                    <label>Quantidade</label>
                    <input
                      type='number'
                      step='any'
                      min='1'
                      value={material.quantityToUse}
                      onChange={(e) =>
                        updateItem(
                          'materials',
                          material.id,
                          'quantityToUse',
                          e.target.value.replace(/[^\d.]/g, ''),
                        )
                      }
                      disabled={material.isAbated}
                      style={{
                        borderColor: material.hasInsufficientStock ? 'red' : undefined,
                        borderWidth: material.hasInsufficientStock ? '2px' : undefined,
                      }}
                    />
                  </StyledFormGroup>
                </MaterialFields>
                <InlineDeleteButton
                  type='button'
                  onClick={() => removeItem('materials', material.id)}
                  disabled={material.isAbated}
                >
                  <img src={trash} alt='Excluir' />
                </InlineDeleteButton>
              </MaterialItemContainer>
            ))}

            <SectionButtons>
              <AddButton
                type='button'
                onClick={addMaterial}
                disabled={!canAddMaterial}
              >
                <img src={plus} alt='Adicionar' />
                {canAddMaterial ? 'Adicionar material' : materialMessage}
              </AddButton>
            </SectionButtons>
          </StyledCard>

          <StyledCard>
            <StyledCardHeader>
              <CardTitle>Insumos</CardTitle>
            </StyledCardHeader>

            {form.resources.map((resource, index) => (
              <MaterialItemContainer key={resource.id}>
                <MaterialFields>
                  <StyledFormGroup className='dropdown-container'>
                    <label>Insumo</label>
                    <input
                      type='text'
                      value={
                        editingResourceIndex === index
                          ? searchResource
                          : resource.resource
                      }
                      onChange={(e) => {
                        setSearchResource(e.target.value)
                        setEditingResourceIndex(index)
                      }}
                      onFocus={() => {
                        setEditingResourceIndex(index)
                        setSearchResource(resource.resource)
                      }}
                      disabled={resource.isAbated}
                    />
                    {editingResourceIndex === index &&
                      resourceOptions.length > 0 && (
                        <DropdownList>
                          {resourceOptions.map((option) => (
                            <DropdownItem
                              key={option.id}
                              onClick={() =>
                                selectItem('resources', index, option)
                              }
                            >
                              {option.name}
                            </DropdownItem>
                          ))}
                        </DropdownList>
                      )}
                  </StyledFormGroup>
                  <StyledFormGroup>
                    <label>Un. de medida</label>
                    <select
                      value={resource.unit}
                      onChange={(e) =>
                        updateItem(
                          'resources',
                          resource.id,
                          'unit',
                          e.target.value,
                        )
                      }
                      disabled={resource.isAbated}
                    >
                      <option value='KG'>KG</option>
                      <option value='G'>G</option>
                      <option value='UN'>UN</option>
                      <option value='MM'>MM</option>
                      <option value='CM'>CM</option>
                      <option value='M'>M</option>
                    </select>
                  </StyledFormGroup>
                  <StyledFormGroup>
                    <label>Quantidade</label>
                    <input
                      type='number'
                      min='0'
                      step={resource.unit === 'UN' ? '1' : '0.01'}
                      value={resource.quantity}
                      onChange={(e) => {
                        let value = e.target.value
                        if (resource.unit === 'UN' && value.includes('.')) {
                          value = value.split('.')[0]
                        }
                        updateItem(
                          'resources',
                          resource.id,
                          'quantity',
                          value,
                        )
                      }}
                      disabled={resource.isAbated}
                    />
                  </StyledFormGroup>
                </MaterialFields>
                <InlineDeleteButton
                  type='button'
                  onClick={() => removeItem('resources', resource.id)}
                  disabled={resource.isAbated}
                >
                  <img src={trash} alt='Excluir' />
                </InlineDeleteButton>
              </MaterialItemContainer>
            ))}

            <SectionButtons>
              <AddButton
                type='button'
                onClick={addResource}
                disabled={!canAddResource}
              >
                <img src={plus} alt='Adicionar' />
                {canAddResource ? 'Adicionar insumo' : resourceMessage}
              </AddButton>
            </SectionButtons>
          </StyledCard>

          <StyledCard>
            <StyledCardHeader>
              <CardTitle>Serviços</CardTitle>
            </StyledCardHeader>

            {form.services.map((service, index) => (
              <MaterialItemContainer key={service.id}>
                <MaterialFields>
                  <StyledFormGroup className='dropdown-container'>
                    <label>Serviço</label>
                    <input
                      type='text'
                      value={
                        editingServiceIndex === index
                          ? searchService
                          : service.service
                      }
                      onChange={(e) => {
                        setSearchService(e.target.value)
                        setEditingServiceIndex(index)
                      }}
                      onFocus={() => {
                        setEditingServiceIndex(index)
                        setSearchService(service.service)
                      }}
                    />
                    {editingServiceIndex === index &&
                      serviceOptions.length > 0 && (
                        <DropdownList>
                          {serviceOptions.map((option) => (
                            <DropdownItem
                              key={option.id}
                              onClick={() =>
                                selectItem('services', index, option)
                              }
                            >
                              {option.name}
                            </DropdownItem>
                          ))}
                        </DropdownList>
                      )}
                  </StyledFormGroup>
                  <StyledFormGroup>
                    <label>Descrição</label>
                    <input
                      type='text'
                      value={service.description}
                      onChange={(e) =>
                        updateItem(
                          'services',
                          service.id,
                          'description',
                          e.target.value,
                        )
                      }
                    />
                  </StyledFormGroup>
                  <StyledFormGroup>
                    <label>Observação</label>
                    <input
                      type='text'
                      value={service.observation}
                      onChange={(e) =>
                        updateItem(
                          'services',
                          service.id,
                          'observation',
                          e.target.value,
                        )
                      }
                    />
                  </StyledFormGroup>
                </MaterialFields>
                <InlineDeleteButton
                  type='button'
                  onClick={() => removeItem('services', service.id)}
                >
                  <img src={trash} alt='Excluir' />
                </InlineDeleteButton>
              </MaterialItemContainer>
            ))}

            <SectionButtons>
              <AddButton type='button' onClick={addService}>
                <img src={plus} alt='Adicionar' />
                Adicionar serviço
              </AddButton>
            </SectionButtons>
          </StyledCard>

          <ActionButtons
            style={{ marginTop: '24px', justifyContent: 'flex-end' }}
          >
            <CancelButton type='button' onClick={handleCancel}>
              Cancelar
            </CancelButton>
            <SaveButton
              type='button'
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              <img src={save} alt='Salvar' />
              {isSubmitting
                ? isEditMode
                  ? 'Atualizando...'
                  : 'Criando OP...'
                : isEditMode
                ? 'Atualizar OP'
                : 'Salvar OP'}
            </SaveButton>
          </ActionButtons>
        </form>
      </MainContainer>
    </>
  )
}

export default CreateOP